import { XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';

import { dismiss, getToasts, subscribeToToasts, type Toast } from '@/lib/toast';
import { cn } from '@/utils/cn';

import { IconButton } from '../ui/IconButton';

export interface ToasterProps {
  /** Already localized. */
  dismissLabel: string;
}

/**
 * In-house (ADR-015). The toast markup itself carries no `aria-live` — announcements go through
 * the app's single polite region via `toast.success`/`toast.error`. Pairing a second live region
 * with `LiveRegion` is the failure that caused `sonner` to be removed.
 *
 * Errors are sticky. Success auto-dismisses. Max three visible.
 */
export function Toaster({ dismissLabel }: ToasterProps) {
  const [items, setItems] = useState<readonly Toast[]>(getToasts);

  useEffect(() => subscribeToToasts(setItems), []);

  if (items.length === 0) return null;

  return (
    <ol
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-30 flex flex-col gap-2',
        'px-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
      )}
    >
      {items.map((item) => (
        <li key={item.id} className="pointer-events-auto mx-auto w-full max-w-md">
          <div
            className={cn(
              'flex items-start gap-3 rounded-lg border px-3 py-3 shadow-none',
              item.variant === 'error'
                ? 'bg-surface text-text border-danger'
                : 'bg-surface text-text border-success',
            )}
          >
            <p className="min-w-0 flex-1 text-sm">{item.message}</p>
            <IconButton
              aria-label={dismissLabel}
              icon={<XMarkIcon />}
              size="sm"
              onClick={() => {
                dismiss(item.id);
              }}
            />
          </div>
        </li>
      ))}
    </ol>
  );
}
